import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, RefreshCw, Search, Trash2, KeyRound, Ban, CheckCircle2, Copy, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

const DOMAIN = "hmgglobalconsults.com";

export default function EntraUsers() {
  const { state, run, refresh } = useAzure();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pw, setPw] = useState(null); // one-time password dialog
  const [form, setForm] = useState({
    display_name: "", mail_nickname: "", upn_prefix: "", auto_pw: true, password: "",
    first_name: "", last_name: "", job_title: "", department: "",
    force_password_change: true,
  });

  const users = state?.entra_users || [];
  const filtered = users.filter((u) => {
    const s = q.toLowerCase();
    return (u.display_name + u.upn + u.department + u.job_title).toLowerCase().includes(s);
  });

  const openCreate = () => {
    setForm({ display_name: "", mail_nickname: "", upn_prefix: "", auto_pw: true, password: "",
      first_name: "", last_name: "", job_title: "", department: "", force_password_change: true });
    setOpen(true);
  };

  const submit = async () => {
    const upn = `${form.upn_prefix.trim()}@${DOMAIN}`;
    const body = {
      display_name: form.display_name.trim(),
      upn,
      password: form.auto_pw ? null : form.password,
      first_name: form.first_name, last_name: form.last_name,
      job_title: form.job_title, department: form.department,
      force_password_change: form.force_password_change,
    };
    try {
      const { data } = await api.post("/azure/entra/users", body);
      await refresh();
      setOpen(false);
      if (data.temp_password) setPw({ upn, password: data.temp_password });
      else toast.success(`Created user ${upn}`);
    } catch (e) {
      toast.error(azErr(e));
    }
  };

  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/azure/entra/users/${u.object_id}/reset-password`);
      await refresh();
      setPw({ upn: u.upn, password: data.temp_password });
      setSelected(null);
    } catch (e) {
      toast.error(azErr(e));
    }
  };

  const block = (u) =>
    run(() => api.post(`/azure/entra/users/${u.object_id}/block`, { blocked: u.account_enabled }),
      { success: u.account_enabled ? "Sign-in blocked" : "Sign-in unblocked" }).then(() => setSelected(null));

  const del = (u) => {
    if (!window.confirm(`Delete user ${u.upn}?`)) return;
    run(() => api.delete(`/azure/entra/users/${u.object_id}`), { success: "User deleted" }).then(() => setSelected(null));
  };

  return (
    <div data-testid="az-entra-users">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3 flex-wrap">
        <Button size="sm" onClick={openCreate} data-testid="az-new-user-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2">
          <UserPlus className="h-4 w-4" /> New user
        </Button>
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="az-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users" className="pl-8 h-9 w-64" />
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Display name</TableHead>
              <TableHead>User principal name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.object_id} className="cursor-pointer" data-testid={`az-user-row-${u.mail_nickname}`} onClick={() => setSelected(u)}>
                <TableCell className="font-medium text-[#0078d4]">{u.display_name}</TableCell>
                <TableCell className="text-muted-foreground">{u.upn}</TableCell>
                <TableCell>{u.department || "—"}</TableCell>
                <TableCell>{u.user_type}</TableCell>
                <TableCell>
                  {u.account_enabled ? (
                    <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">Enabled</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 text-xs font-medium">Blocked</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No users found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Display name *</Label>
                <Input data-testid="az-user-displayname" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Sarah Johnson" />
              </div>
              <div className="space-y-1.5">
                <Label>User principal name *</Label>
                <div className="flex items-center">
                  <Input data-testid="az-user-upn" value={form.upn_prefix} onChange={(e) => setForm({ ...form, upn_prefix: e.target.value })} placeholder="sarah.johnson" className="rounded-r-none" />
                  <span className="text-xs text-muted-foreground border border-l-0 border-border rounded-r-md px-2 h-9 grid place-items-center bg-muted">@{DOMAIN}</span>
                </div>
              </div>
              <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Job title</Label><Input data-testid="az-user-jobtitle" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Financial Analyst" /></div>
              <div className="space-y-1.5"><Label>Department</Label><Input data-testid="az-user-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Finance" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.auto_pw} onChange={(e) => setForm({ ...form, auto_pw: e.target.checked })} data-testid="az-user-autopw" />
              Auto-generate password
            </label>
            {!form.auto_pw && (
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Initial password" />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.force_password_change} onChange={(e) => setForm({ ...form, force_password_change: e.target.checked })} />
              Require password change at next sign-in
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="az-user-create-submit" onClick={submit} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time password dialog */}
      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent className="max-w-md" data-testid="az-temp-password-dialog">
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copy this password now — for security it will not be shown again. A new reset is required to see it later.</p>
          <div className="mt-2 rounded-md border border-border p-3">
            <div className="text-xs text-muted-foreground">{pw?.upn}</div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <code className="font-mono text-base">{pw?.password}</code>
              <Button size="sm" variant="outline" className="gap-2" onClick={() => { navigator.clipboard.writeText(pw.password); toast.success("Copied"); }}>
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setPw(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details pane */}
      {selected && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-user-detail">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="font-display font-bold text-lg">{selected.display_name}</div>
              <div className="text-xs text-muted-foreground">{selected.upn}</div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border flex-wrap">
            <Button size="sm" variant="outline" className="gap-2" data-testid="az-reset-pw-btn" onClick={() => resetPw(selected)}><KeyRound className="h-4 w-4" /> Reset password</Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => block(selected)}>
              {selected.account_enabled ? <><Ban className="h-4 w-4" /> Block</> : <><CheckCircle2 className="h-4 w-4" /> Unblock</>}
            </Button>
            <Button size="sm" variant="outline" className="gap-2 text-red-600" onClick={() => del(selected)}><Trash2 className="h-4 w-4" /> Delete</Button>
          </div>
          <div className="p-5 space-y-3 text-sm overflow-y-auto">
            {[["Object ID", selected.object_id], ["User type", selected.user_type], ["Department", selected.department || "—"],
              ["Job title", selected.job_title || "—"], ["Company", selected.company || "—"], ["Sync", selected.sync_type],
              ["Account enabled", selected.account_enabled ? "Yes" : "No"],
              ["Password resets", selected.password_reset_count],
              ["Force change at sign-in", selected.force_password_change ? "Yes" : "No"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-border/50 pb-2">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium text-right break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
